import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "react-toastify";
import { api } from "../api";
import {
  AlertTriangle,
  CalendarCheck,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  Plus,
  ShieldCheck,
  Trash2,
  X,
} from "lucide-react";

const STATUS_AGENDAMENTO = [
  { label: "Agendado", color: "#3768C4" },
  { label: "Confirmado", color: "#5B4FE0" },
  { label: "Reagendado", color: "#7C5CE7" },
  { label: "Compareceu", color: "#1F9D6B" },
  { label: "Faltou", color: "#D6455C" },
  { label: "Cancelado", color: "#64748B" },
];

// Mantidos apenas para leitura de históricos antigos.
const STATUS_AGENDAMENTO_LEGADO = [
  { label: "Sem Contato", color: "#6B7280" },
  { label: "Pendência", color: "#C08A1E" },
  { label: "Pendente", color: "#C08A1E" },
];

const FLUXO_STATUS_AGENDAMENTO = {
  inicial: ["Agendado"],
  Agendado: ["Confirmado", "Reagendado", "Compareceu", "Faltou", "Cancelado"],
  Confirmado: ["Reagendado", "Compareceu", "Faltou", "Cancelado"],
  Reagendado: ["Confirmado", "Reagendado", "Compareceu", "Faltou", "Cancelado"],
  Compareceu: ["Agendado"],
  Faltou: ["Agendado"],
  Cancelado: ["Agendado"],
};

const PERM_DELETE_AG = "agendamento.delete.current_or_future";
const PERM_DELETE_AG_ANY = "agendamento.delete.any_month";

const PERM_DELETE_AG_ALIASES = [
  PERM_DELETE_AG,
  "agendamentos.excluir",
  "agendamento.excluir",
  "agenda.excluir",
  "agenda.delete",
  "agendamentos.delete",
  "agendamentos.delete.current_or_future",
  "delete_agendamento",
  "excluir_agendamento",
  "excluir_agendamentos",
];

const PERM_DELETE_AG_ANY_ALIASES = [
  PERM_DELETE_AG_ANY,
  "agendamentos.excluir.qualquer_mes",
  "agendamento.excluir.qualquer_mes",
  "agendamentos.delete.any_month",
  "agenda.delete.any_month",
  "delete_agendamento_any_month",
  "excluir_agendamento_qualquer_mes",
  "excluir_agendamentos_qualquer_mes",
];

const ITENS_EXTRATO = 3;
const ITENS_AUDITORIA = 2;

const C = {
  bg: "#FFFFFF",
  panel: "#F7F8FA",
  panelSoft: "#F1F0FB",
  line: "#E7E9EF",
  lineSoft: "#EEF0F4",
  text: "#1B1D27",
  textSoft: "#5B5F72",
  textMuted: "#8A8E9E",
  accent: "#5B4FE0",
  accentSoft: "#6C5CE8",
  rose: "#D6455C",
  amber: "#C08A1E",
  emerald: "#1F9D6B",
  blue: "#3768C4",
};

function getUsuarioLogadoLocal() {
  try {
    return JSON.parse(localStorage.getItem("usuarioLogado") || "{}");
  } catch {
    return {};
  }
}

function getLeadId(lead) {
  return lead?.id || lead?.lead_id || lead?.id_lead || lead?.idLead || null;
}

function getEmpresaIdEfetiva(lead, usuarioLogado) {
  return (
    lead?.empresa_id ||
    lead?.empresaId ||
    lead?.id_empresa ||
    lead?.empresa?.id ||
    usuarioLogado?.empresa_id ||
    usuarioLogado?.empresaId ||
    usuarioLogado?.id_empresa ||
    null
  );
}

function parseLocalDateTime(value) {
  if (!value) return null;
  if (value instanceof Date && !Number.isNaN(value.getTime())) return value;
  if (typeof value !== "string") return null;

  const str = value.replace("T", " ").trim();
  const [datePart, timePart = "00:00:00"] = str.split(" ");
  const [Y, M, D] = String(datePart || "").split("-").map(Number);
  const [h = 0, m = 0, s = 0] = String(timePart || "").split(":").map(Number);

  if (!Y || !M || !D) return null;

  const dt = new Date(Y, M - 1, D, h || 0, m || 0, s || 0);
  return Number.isNaN(dt.getTime()) ? null : dt;
}

function dateOrderNum(value) {
  const dt = parseLocalDateTime(value);
  return dt ? dt.getTime() : 0;
}

function formatDataHora(value) {
  const dt = parseLocalDateTime(value);
  if (!dt) return "—";

  return `${dt.toLocaleDateString("pt-BR")} ${dt.toLocaleTimeString("pt-BR", {
    hour: "2-digit",
    minute: "2-digit",
  })}`;
}

function formatDataCurta(value) {
  const dt = parseLocalDateTime(value);
  if (!dt) return "—";

  return dt.toLocaleDateString("pt-BR", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  });
}

function normalizeDateTimeInput(value) {
  if (!value) return "";
  const base = String(value).replace("T", " ");
  return base.length === 16 ? `${base}:00` : base;
}

function isCurrentOrFutureMonth(value) {
  const dt = parseLocalDateTime(value);
  if (!dt) return false;

  const agora = new Date();
  const atual = new Date(agora.getFullYear(), agora.getMonth(), 1);
  const registro = new Date(dt.getFullYear(), dt.getMonth(), 1);

  return registro.getTime() >= atual.getTime();
}

function normalizaPermissoes(resposta) {
  let raw = resposta;

  if (raw && typeof raw === "object" && "permissoes" in raw) {
    raw = raw.permissoes;
  }

  if (typeof raw === "string") {
    try {
      raw = JSON.parse(raw);
    } catch {
      raw = {};
    }
  }

  if (Array.isArray(raw)) {
    raw = raw.reduce((acc, item) => {
      if (typeof item === "string") acc[item] = true;
      else if (item?.chave) acc[item.chave] = Boolean(item.permitido ?? true);
      return acc;
    }, {});
  }

  if (!raw || typeof raw !== "object") raw = {};

  const normalizado = { ...raw };

  const podeExcluirMesAtual = PERM_DELETE_AG_ALIASES.some((chave) =>
    Boolean(raw[chave]),
  );

  const podeExcluirQualquerMes = PERM_DELETE_AG_ANY_ALIASES.some((chave) =>
    Boolean(raw[chave]),
  );

  for (const chave of PERM_DELETE_AG_ALIASES) {
    normalizado[chave] = podeExcluirMesAtual;
  }

  for (const chave of PERM_DELETE_AG_ANY_ALIASES) {
    normalizado[chave] = podeExcluirQualquerMes;
  }

  return normalizado;
}

function temPermissao(permissoes = {}, chaves = []) {
  return chaves.some((chave) => Boolean(permissoes?.[chave]));
}

function normalizarPerfilUsuario(value = "") {
  return String(value || "")
    .trim()
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "");
}

// Auditoria é propositalmente restrita a ADMINISTRADOR.
// Supervisor/gestor/gerente não entram automaticamente.
function isUsuarioAdministrador(usuario = {}) {
  if (!usuario || typeof usuario !== "object") return false;

  const flagsAdmin = [
    usuario.admin,
    usuario.is_admin,
    usuario.isAdmin,
    usuario.administrador,
    usuario.isAdministrador,
    usuario.super_admin,
    usuario.superAdmin,
    usuario.master,
    usuario.root,
  ];

  if (
    flagsAdmin.some(
      (value) =>
        value === true ||
        value === 1 ||
        String(value || "").toLowerCase() === "true",
    )
  ) {
    return true;
  }

  const perfil = normalizarPerfilUsuario(
    [
      usuario.perfil,
      usuario.tipo,
      usuario.tipo_usuario,
      usuario.tipoUsuario,
      usuario.role,
      usuario.cargo,
      usuario.nivel,
      usuario.grupo,
    ]
      .filter(Boolean)
      .join(" "),
  );

  return (
    perfil.includes("administrador") ||
    perfil === "admin" ||
    perfil.includes("super admin") ||
    perfil.includes("super_admin") ||
    perfil === "master" ||
    perfil === "root"
  );
}

function getStatusAgendamentoConfig(label) {
  return (
    STATUS_AGENDAMENTO.find((status) => status.label === label) ||
    STATUS_AGENDAMENTO_LEGADO.find((status) => status.label === label) || {
      label: label || "Sem status",
      color: "#8A8E9E",
    }
  );
}

function corStatus(status) {
  const s = String(status || "").toLowerCase();

  if (s.includes("cancel") || s.includes("falt")) {
    return { color: C.rose, bg: "#FBE9EC" };
  }

  if (s.includes("confirm")) {
    return { color: C.accent, bg: "#EEEBFB" };
  }

  if (
    s.includes("comparec") ||
    s.includes("realiz") ||
    s.includes("conclu") ||
    s.includes("atendid")
  ) {
    return { color: C.emerald, bg: "#E7F6EF" };
  }

  if (s.includes("reagend")) {
    return { color: "#7C5CE7", bg: "#F0ECFD" };
  }

  if (!s) {
    return { color: C.textMuted, bg: C.panel };
  }

  return { color: C.blue, bg: "#EAF1FC" };
}

function getHorarioEvento(item = {}) {
  return (
    item.criado_em ||
    item.data_lancamento ||
    item.created_at ||
    item.updated_at ||
    item.atualizado_em ||
    null
  );
}

function ordenarMovimentos(lista = []) {
  return [...(Array.isArray(lista) ? lista : [])]
    .filter(Boolean)
    .sort((a, b) => {
      const eventoA = dateOrderNum(getHorarioEvento(a));
      const eventoB = dateOrderNum(getHorarioEvento(b));

      if (eventoA !== eventoB) return eventoB - eventoA;

      const idA = Number(a?.id || 0);
      const idB = Number(b?.id || 0);
      if (idA !== idB) return idB - idA;

      return dateOrderNum(b?.data_agendamento) - dateOrderNum(a?.data_agendamento);
    });
}

function ordenarAuditoria(lista = []) {
  return [...(Array.isArray(lista) ? lista : [])]
    .filter(Boolean)
    .sort((a, b) => {
      const dataA = dateOrderNum(a?.criado_em);
      const dataB = dateOrderNum(b?.criado_em);

      if (dataA !== dataB) return dataB - dataA;

      return Number(b?.id || 0) - Number(a?.id || 0);
    });
}

function getStatusPermitidosAgendamento(historico = []) {
  const ultimo = ordenarMovimentos(historico)[0];
  const ultimoStatus = String(ultimo?.status_agendamento || "").trim();

  const labels =
    FLUXO_STATUS_AGENDAMENTO[ultimoStatus] ||
    FLUXO_STATUS_AGENDAMENTO.inicial;

  return labels.map(getStatusAgendamentoConfig);
}

function extrairAgendamentoDaResposta(response, payload) {
  const data = response?.data ?? response;

  const agendamento =
    data?.agendamento ||
    data?.data?.agendamento ||
    data?.data ||
    data?.registro ||
    data?.result ||
    data ||
    {};

  return {
    ...payload,
    ...(agendamento && typeof agendamento === "object" ? agendamento : {}),
    id:
      agendamento?.id ||
      agendamento?.insertId ||
      data?.id ||
      data?.insertId ||
      payload?.id ||
      null,
    lead_id:
      agendamento?.lead_id ||
      agendamento?.id_lead ||
      payload?.lead_id ||
      payload?.id_lead,
    id_lead:
      agendamento?.id_lead ||
      agendamento?.lead_id ||
      payload?.id_lead ||
      payload?.lead_id,
    empresa_id: agendamento?.empresa_id || payload?.empresa_id,
    data_agendamento:
      agendamento?.data_agendamento || payload?.data_agendamento,
    status_agendamento:
      agendamento?.status_agendamento || payload?.status_agendamento,
    observacoes:
      agendamento?.observacoes ||
      agendamento?.observacao ||
      payload?.observacoes ||
      "",
    nome_usuario:
      agendamento?.nome_usuario ||
      agendamento?.usuario_nome ||
      payload?.nome_usuario ||
      "",
  };
}

function getNomeLead(lead) {
  return (
    lead?.nome_lead ||
    lead?.nome ||
    lead?.nomeLead ||
    lead?.paciente_nome ||
    "Lead"
  );
}

function descricaoMudancaAuditoria(item = {}) {
  const anterior = item?.status_anterior || "—";
  const novo = item?.status_novo || item?.status_agendamento || "—";

  const dataAnterior =
    item?.data_agendamento_anterior ||
    item?.data_anterior ||
    item?.agendamento_anterior ||
    null;

  const dataNova =
    item?.data_agendamento_nova ||
    item?.data_nova ||
    item?.agendamento_novo ||
    null;

  return {
    statusAnterior: anterior,
    statusNovo: novo,
    dataAnterior,
    dataNova,
  };
}

export default function AgendamentosModal({
  isOpen,
  onClose,
  lead,
  onSubmit,
  onUltimoAgendamento,
  onAgendamentoSalvo,
}) {
  const usuarioLogado = useMemo(() => getUsuarioLogadoLocal(), []);
  const leadId = getLeadId(lead);
  const empresaIdEfetiva = getEmpresaIdEfetiva(lead, usuarioLogado);

  const usuarioId =
    usuarioLogado?.id || usuarioLogado?.usuario_id || null;

  const usuarioNome =
    usuarioLogado?.nome || usuarioLogado?.name || "";

  const usuarioEhAdministrador = useMemo(
    () => isUsuarioAdministrador(usuarioLogado),
    [usuarioLogado],
  );

  const [novoAgendamento, setNovoAgendamento] = useState({
    data_agendamento: "",
    observacao: "",
    status_agendamento: "Agendado",
  });

  const [historico, setHistorico] = useState([]);
  const [auditoria, setAuditoria] = useState([]);
  const [loading, setLoading] = useState(false);
  const [salvando, setSalvando] = useState(false);
  const [erro, setErro] = useState("");
  const [permissoes, setPermissoes] = useState({});
  const [paginaExtrato, setPaginaExtrato] = useState(1);
  const [paginaAuditoria, setPaginaAuditoria] = useState(1);
  const [auditoriaAberta, setAuditoriaAberta] = useState(false);

  // IMPORTANTE:
  // O LeadSpeed passa alguns callbacks inline. Se eles entrarem nas dependências
  // de carregarHistorico/useEffect, cada atualização da linha recria o callback,
  // dispara nova busca e pode gerar um ciclo visual de "piscar".
  // Guardamos as versões mais recentes em refs para quebrar esse ciclo.
  const onUltimoAgendamentoRef = useRef(onUltimoAgendamento);
  const onAgendamentoSalvoRef = useRef(onAgendamentoSalvo);
  const onSubmitRef = useRef(onSubmit);

  useEffect(() => {
    onUltimoAgendamentoRef.current = onUltimoAgendamento;
  }, [onUltimoAgendamento]);

  useEffect(() => {
    onAgendamentoSalvoRef.current = onAgendamentoSalvo;
  }, [onAgendamentoSalvo]);

  useEffect(() => {
    onSubmitRef.current = onSubmit;
  }, [onSubmit]);

  const historicoOrdenado = useMemo(
    () => ordenarMovimentos(historico),
    [historico],
  );

  const auditoriaOrdenada = useMemo(
    () => ordenarAuditoria(auditoria),
    [auditoria],
  );

  const statusPermitidos = useMemo(
    () => getStatusPermitidosAgendamento(historicoOrdenado),
    [historicoOrdenado],
  );

  const ultimoMovimento = historicoOrdenado[0] || null;

  const executorUltimo =
    ultimoMovimento?.nome_usuario ||
    ultimoMovimento?.usuario_nome ||
    ultimoMovimento?.nomeUsuario ||
    "Não identificado";

  const corUltimoStatus = corStatus(
    ultimoMovimento?.status_agendamento,
  );

  const totalPaginasExtrato = Math.max(
    1,
    Math.ceil(historicoOrdenado.length / ITENS_EXTRATO),
  );

  const paginaExtratoSegura = Math.min(
    paginaExtrato,
    totalPaginasExtrato,
  );

  const inicioExtrato =
    (paginaExtratoSegura - 1) * ITENS_EXTRATO;

  const itensExtrato = historicoOrdenado.slice(
    inicioExtrato,
    inicioExtrato + ITENS_EXTRATO,
  );

  const totalPaginasAuditoria = Math.max(
    1,
    Math.ceil(auditoriaOrdenada.length / ITENS_AUDITORIA),
  );

  const paginaAuditoriaSegura = Math.min(
    paginaAuditoria,
    totalPaginasAuditoria,
  );

  const inicioAuditoria =
    (paginaAuditoriaSegura - 1) * ITENS_AUDITORIA;

  const itensAuditoria = auditoriaOrdenada.slice(
    inicioAuditoria,
    inicioAuditoria + ITENS_AUDITORIA,
  );

  const carregarPermissoes = useCallback(async () => {
    if (!isOpen || !usuarioId || !empresaIdEfetiva) {
      setPermissoes({});
      return;
    }

    try {
      const res = await api.get(
        `/permissoes/${usuarioId}/${empresaIdEfetiva}`,
      );

      setPermissoes(
        normalizaPermissoes(res?.data ?? res),
      );
    } catch {
      setPermissoes({});
    }
  }, [isOpen, usuarioId, empresaIdEfetiva]);

  const carregarAuditoria = useCallback(async () => {
    if (
      !isOpen ||
      !usuarioEhAdministrador ||
      !leadId ||
      !empresaIdEfetiva
    ) {
      setAuditoria([]);
      return;
    }

    try {
      const res = await api.get(
        `/agendamentos/historico-status/${empresaIdEfetiva}`,
        {
          params: {
            lead_id: leadId,
            limit: 100,
            ...(usuarioId ? { usuario_id: usuarioId } : {}),
            ...(usuarioNome ? { usuario_nome: usuarioNome } : {}),
          },
        },
      );

      const raw = res?.data ?? res;

      const lista = Array.isArray(raw)
        ? raw
        : Array.isArray(raw?.data)
          ? raw.data
          : Array.isArray(raw?.historico)
            ? raw.historico
            : [];

      setAuditoria(lista);
    } catch (err) {
      console.warn(
        "AgendamentosModal | auditoria indisponível:",
        err?.response?.data?.error ||
          err?.response?.data?.erro ||
          err?.message ||
          err,
      );
      setAuditoria([]);
    }
  }, [
    isOpen,
    usuarioEhAdministrador,
    leadId,
    empresaIdEfetiva,
    usuarioId,
    usuarioNome,
  ]);

  const carregarHistorico = useCallback(async () => {
    if (!isOpen || !leadId) return;

    setLoading(true);
    setErro("");

    try {
      const res = await api.get(`/agendamentos/lead/${leadId}`, {
        params: empresaIdEfetiva
          ? { empresa_id: empresaIdEfetiva }
          : {},
      });

      const raw = res?.data ?? res;

      const lista = Array.isArray(raw)
        ? raw
        : Array.isArray(raw?.data)
          ? raw.data
          : Array.isArray(raw?.agendamentos)
            ? raw.agendamentos
            : [];

      const ordenado = ordenarMovimentos(lista);

      setHistorico(ordenado);
      setPaginaExtrato(1);
      onUltimoAgendamentoRef.current?.(ordenado[0] || null);

      if (usuarioEhAdministrador) {
        await carregarAuditoria();
      } else {
        setAuditoria([]);
      }
    } catch (err) {
      const msg =
        err?.response?.data?.erro ||
        err?.response?.data?.error ||
        err?.response?.data?.message ||
        err?.message ||
        "Erro ao carregar histórico de agendamentos.";

      setErro(msg);
      setHistorico([]);
      onUltimoAgendamentoRef.current?.(null);
    } finally {
      setLoading(false);
    }
  }, [
    isOpen,
    leadId,
    empresaIdEfetiva,
    usuarioEhAdministrador,
    carregarAuditoria,
  ]);

  useEffect(() => {
    if (!isOpen) return;

    setNovoAgendamento({
      data_agendamento: "",
      observacao: "",
      status_agendamento: "Agendado",
    });
    setPaginaExtrato(1);
    setPaginaAuditoria(1);
    setAuditoriaAberta(false);
    setErro("");

    carregarPermissoes();
    carregarHistorico();
  }, [
    isOpen,
    leadId,
    empresaIdEfetiva,
    carregarPermissoes,
    carregarHistorico,
  ]);

  useEffect(() => {
    if (!isOpen) return;

    const atual = novoAgendamento.status_agendamento;
    const permitido = statusPermitidos.some(
      (status) => status.label === atual,
    );

    if (!permitido) {
      setNovoAgendamento((prev) => ({
        ...prev,
        status_agendamento:
          statusPermitidos[0]?.label || "Agendado",
      }));
    }
  }, [
    isOpen,
    statusPermitidos,
    novoAgendamento.status_agendamento,
  ]);

  if (!isOpen || !leadId) return null;

  const canDeleteAg = (dataAgendamento) => {
    if (!usuarioId) return false;

    if (usuarioEhAdministrador) return true;

    if (
      temPermissao(
        permissoes,
        PERM_DELETE_AG_ANY_ALIASES,
      )
    ) {
      return true;
    }

    return (
      temPermissao(
        permissoes,
        PERM_DELETE_AG_ALIASES,
      ) &&
      isCurrentOrFutureMonth(dataAgendamento)
    );
  };

  const salvarAgendamento = async () => {
    if (!novoAgendamento.data_agendamento) {
      toast.warning("Informe a data e hora do agendamento.");
      return;
    }

    if (!empresaIdEfetiva) {
      toast.error(
        "Não foi possível identificar a empresa do agendamento.",
      );
      return;
    }

    const statusPermitido = statusPermitidos.some(
      (status) =>
        status.label ===
        novoAgendamento.status_agendamento,
    );

    if (!statusPermitido) {
      toast.warning(
        "Esse status não é permitido para a etapa atual.",
      );
      return;
    }

    const payload = {
      empresa_id: empresaIdEfetiva,
      empresaId: empresaIdEfetiva,
      id_empresa: empresaIdEfetiva,

      lead_id: leadId,
      id_lead: leadId,
      leadId,

      data_agendamento: normalizeDateTimeInput(
        novoAgendamento.data_agendamento,
      ),

      status_agendamento:
        novoAgendamento.status_agendamento,

      observacoes: novoAgendamento.observacao,
      observacao: novoAgendamento.observacao,

      usuario_criador_id: usuarioId,
      usuario_id: usuarioId,
      criado_por_id: usuarioId,
      nome_usuario: usuarioNome,
      usuario_nome: usuarioNome,
    };

    setSalvando(true);
    setErro("");

    try {
      const response = await api.post(
        "/agendamentos",
        payload,
      );

      const salvo = extrairAgendamentoDaResposta(
        response,
        payload,
      );

      toast.success("Movimento da agenda salvo!");

      setNovoAgendamento({
        data_agendamento: "",
        observacao: "",
        status_agendamento: "Agendado",
      });

      setPaginaExtrato(1);

      await carregarHistorico();

      onAgendamentoSalvoRef.current?.(salvo);
      onSubmitRef.current?.(salvo);
    } catch (err) {
      const msg =
        err?.response?.data?.erro ||
        err?.response?.data?.error ||
        err?.response?.data?.message ||
        err?.message ||
        "Erro ao salvar agendamento.";

      setErro(msg);
      toast.error(msg);
      console.error(
        "AgendamentosModal | erro ao salvar:",
        err,
      );
    } finally {
      setSalvando(false);
    }
  };

  const excluirAgendamento = async (id) => {
    if (!id || salvando) return;

    if (!usuarioId) {
      toast.error(
        "Usuário logado não identificado para exclusão.",
      );
      return;
    }

    if (
      !window.confirm(
        "Excluir este movimento da agenda?",
      )
    ) {
      return;
    }

    setSalvando(true);
    setErro("");

    try {
      await api.delete(`/agendamentos/${id}`, {
        params: {
          usuario_id: usuarioId,
          usuario_nome: usuarioNome || undefined,
          empresa_id: empresaIdEfetiva,
          lead_id: leadId,
        },
      });

      toast.success("Movimento excluído.");

      setPaginaExtrato(1);

      await carregarHistorico();

      onSubmitRef.current?.();
      onAgendamentoSalvoRef.current?.();
    } catch (err) {
      const status = err?.response?.status;

      const msg =
        err?.response?.data?.erro ||
        err?.response?.data?.error ||
        err?.response?.data?.message ||
        err?.message ||
        "Erro ao excluir agendamento.";

      setErro(msg);

      if (status === 403) toast.warn(msg);
      else if (status === 404) {
        toast.info("Agendamento não encontrado.");
      } else {
        toast.error(msg);
      }
    } finally {
      setSalvando(false);
    }
  };

  return (
    <AnimatePresence>
      <motion.div
        className="fixed inset-0 z-[100] flex items-center justify-center p-3"
        style={{
          background: "rgba(20,20,30,0.55)",
          backdropFilter: "blur(2px)",
        }}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        onClick={onClose}
      >
        <motion.div
          className="relative w-full max-w-[800px] overflow-hidden rounded-[20px]"
          style={{
            background: C.bg,
            boxShadow:
              "0 40px 80px -20px rgba(20,20,30,0.5), 0 0 0 1px rgba(0,0,0,0.04)",
          }}
          initial={{ scale: 0.98, y: 14, opacity: 0 }}
          animate={{ scale: 1, y: 0, opacity: 1 }}
          exit={{ scale: 0.98, y: 14, opacity: 0 }}
          transition={{
            type: "spring",
            stiffness: 320,
            damping: 28,
          }}
          onClick={(event) => event.stopPropagation()}
        >
          <div
            className="relative flex items-center gap-4 px-6 py-5 md:px-8"
            style={{
              background: `linear-gradient(120deg, ${C.accent}, ${C.accentSoft})`,
            }}
          >
            <div
              className="flex h-11 w-11 shrink-0 items-center justify-center rounded-[13px]"
              style={{
                background: "rgba(255,255,255,0.16)",
                border:
                  "1px solid rgba(255,255,255,0.25)",
              }}
            >
              <CalendarCheck size={21} color="#fff" />
            </div>

            <div className="min-w-0 flex-1">
              <div
                className="text-[10px] font-bold uppercase tracking-[0.12em]"
                style={{
                  color: "rgba(255,255,255,0.82)",
                }}
              >
                Agenda do paciente
              </div>

              <div className="mt-0.5 truncate text-[20px] font-extrabold text-white">
                {getNomeLead(lead)}
              </div>
            </div>

            <button
              type="button"
              onClick={onClose}
              disabled={salvando}
              className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full transition hover:bg-white/25 disabled:opacity-60"
              style={{
                background: "rgba(255,255,255,0.12)",
                border:
                  "1px solid rgba(255,255,255,0.25)",
              }}
              title="Fechar"
            >
              <X size={16} color="#fff" />
            </button>
          </div>

          <div className="max-h-[78vh] overflow-y-auto px-5 py-5 md:px-8">
            {ultimoMovimento ? (
              <div className="mb-5 grid grid-cols-2 gap-3 sm:grid-cols-4">
                <div
                  className="min-w-0 rounded-xl px-3.5 py-3"
                  style={{
                    background: C.panel,
                    border: `1px solid ${C.line}`,
                  }}
                >
                  <div
                    className="text-[9.5px] font-bold uppercase tracking-wider"
                    style={{ color: C.textMuted }}
                  >
                    Situação atual
                  </div>

                  <div className="mt-1.5">
                    <span
                      className="inline-flex max-w-full items-center gap-1.5 rounded-full px-2.5 py-1 text-[10.5px] font-bold"
                      style={{
                        color: corUltimoStatus.color,
                        background: corUltimoStatus.bg,
                      }}
                    >
                      <span
                        className="h-1.5 w-1.5 shrink-0 rounded-full"
                        style={{
                          background:
                            corUltimoStatus.color,
                        }}
                      />
                      <span className="truncate">
                        {ultimoMovimento.status_agendamento ||
                          "—"}
                      </span>
                    </span>
                  </div>
                </div>

                <div
                  className="min-w-0 rounded-xl px-3.5 py-3"
                  style={{
                    background: C.panel,
                    border: `1px solid ${C.line}`,
                  }}
                >
                  <div
                    className="text-[9.5px] font-bold uppercase tracking-wider"
                    style={{ color: C.textMuted }}
                  >
                    Consulta
                  </div>

                  <div
                    className="mt-1.5 truncate text-[12.5px] font-bold"
                    style={{ color: C.text }}
                    title={formatDataHora(
                      ultimoMovimento.data_agendamento,
                    )}
                  >
                    {formatDataHora(
                      ultimoMovimento.data_agendamento,
                    )}
                  </div>
                </div>

                <div
                  className="min-w-0 rounded-xl px-3.5 py-3"
                  style={{
                    background: C.panel,
                    border: `1px solid ${C.line}`,
                  }}
                >
                  <div
                    className="text-[9.5px] font-bold uppercase tracking-wider"
                    style={{ color: C.textMuted }}
                  >
                    Última movimentação
                  </div>

                  <div
                    className="mt-1.5 truncate text-[12.5px] font-bold"
                    style={{ color: C.text }}
                  >
                    {formatDataHora(
                      getHorarioEvento(ultimoMovimento),
                    )}
                  </div>
                </div>

                <div
                  className="min-w-0 rounded-xl px-3.5 py-3"
                  style={{
                    background: C.panel,
                    border: `1px solid ${C.line}`,
                  }}
                >
                  <div
                    className="text-[9.5px] font-bold uppercase tracking-wider"
                    style={{ color: C.textMuted }}
                  >
                    Executado por
                  </div>

                  <div
                    className="mt-1.5 truncate text-[12.5px] font-bold"
                    style={{ color: C.text }}
                    title={executorUltimo}
                  >
                    {executorUltimo}
                  </div>
                </div>
              </div>
            ) : null}

            <div
              className="rounded-2xl p-4"
              style={{
                background: C.panelSoft,
                border: "1px solid #E3DFFA",
              }}
            >
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                <div className="min-w-0">
                  <label
                    className="mb-1.5 block text-[10px] font-bold uppercase tracking-wider"
                    style={{ color: C.accent }}
                  >
                    Data e hora da consulta
                  </label>

                  <input
                    type="datetime-local"
                    value={novoAgendamento.data_agendamento}
                    onChange={(event) =>
                      setNovoAgendamento((prev) => ({
                        ...prev,
                        data_agendamento:
                          event.target.value,
                      }))
                    }
                    disabled={salvando}
                    className="h-10 w-full rounded-[9px] border px-3 text-[13px] font-semibold outline-none disabled:opacity-60"
                    style={{
                      background: "#fff",
                      borderColor: "#D9D6EF",
                      color: C.text,
                    }}
                  />
                </div>

                <div className="min-w-0">
                  <label
                    className="mb-1.5 block text-[10px] font-bold uppercase tracking-wider"
                    style={{ color: C.accent }}
                  >
                    Status
                  </label>

                  <select
                    value={
                      novoAgendamento.status_agendamento
                    }
                    onChange={(event) =>
                      setNovoAgendamento((prev) => ({
                        ...prev,
                        status_agendamento:
                          event.target.value,
                      }))
                    }
                    disabled={salvando}
                    className="h-10 w-full rounded-[9px] border px-3 text-[13px] font-semibold outline-none disabled:opacity-60"
                    style={{
                      background: "#fff",
                      borderColor: "#D9D6EF",
                      color: C.text,
                    }}
                  >
                    {statusPermitidos.map((status) => (
                      <option
                        key={status.label}
                        value={status.label}
                      >
                        {status.label}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-[minmax(0,1fr)_auto]">
                <div className="min-w-0">
                  <label
                    className="mb-1.5 block text-[10px] font-bold uppercase tracking-wider"
                    style={{ color: C.accent }}
                  >
                    Observação
                  </label>

                  <input
                    type="text"
                    value={novoAgendamento.observacao}
                    onChange={(event) =>
                      setNovoAgendamento((prev) => ({
                        ...prev,
                        observacao: event.target.value,
                      }))
                    }
                    disabled={salvando}
                    placeholder="Observação opcional"
                    className="h-10 w-full rounded-[9px] border px-3 text-[13px] outline-none disabled:opacity-60"
                    style={{
                      background: "#fff",
                      borderColor: "#D9D6EF",
                      color: C.text,
                    }}
                  />
                </div>

                <button
                  type="button"
                  onClick={salvarAgendamento}
                  disabled={salvando}
                  className="mt-auto flex h-10 items-center justify-center gap-1.5 rounded-[9px] px-5 text-[13px] font-bold text-white transition hover:brightness-110 disabled:opacity-60"
                  style={{
                    background: `linear-gradient(90deg, ${C.accent}, ${C.accentSoft})`,
                    boxShadow: `0 8px 18px -6px ${C.accent}88`,
                  }}
                >
                  {salvando ? (
                    "Salvando…"
                  ) : (
                    <>
                      <Plus size={14} />
                      Lançar
                    </>
                  )}
                </button>
              </div>

              <div
                className="mt-2 text-[10px] font-semibold"
                style={{ color: C.textMuted }}
              >
                Ações disponíveis conforme o último
                status registrado.
              </div>
            </div>

            {erro ? (
              <div
                className="mt-3 flex items-center gap-2 rounded-lg px-3.5 py-2.5 text-[12px] font-semibold"
                style={{
                  color: C.rose,
                  background: "#FBE9EC",
                  border: "1px solid #F2C3CB",
                }}
              >
                <AlertTriangle
                  size={14}
                  className="shrink-0"
                />
                <span>{erro}</span>
              </div>
            ) : null}

            <div className="mb-3 mt-6 flex items-center justify-between gap-3">
              <div className="flex items-center gap-2.5">
                <span
                  className="text-[15px] font-extrabold"
                  style={{ color: C.text }}
                >
                  Extrato da agenda
                </span>

                <span
                  className="rounded-full px-2.5 py-1 text-[10px] font-bold"
                  style={{
                    background: "#EEEBFB",
                    color: C.accent,
                  }}
                >
                  {historicoOrdenado.length} movimento
                  {historicoOrdenado.length === 1
                    ? ""
                    : "s"}
                </span>
              </div>

              <span
                className="text-[10.5px] font-semibold"
                style={{ color: C.textMuted }}
              >
                mais recente primeiro
              </span>
            </div>

            {loading ? (
              <div
                className="py-8 text-center text-xs"
                style={{ color: C.textSoft }}
              >
                Carregando agenda...
              </div>
            ) : historicoOrdenado.length === 0 ? (
              <div
                className="rounded-xl border-[1.5px] border-dashed py-7 text-center text-[12px] italic"
                style={{
                  color: C.textMuted,
                  borderColor: C.line,
                }}
              >
                Nenhum movimento de agenda registrado.
              </div>
            ) : (
              <div className="relative space-y-2.5 pl-5">
                <div
                  className="absolute bottom-1.5 left-[5px] top-1.5 w-px"
                  style={{
                    background: `linear-gradient(${C.accent}88, ${C.line} 75%, transparent)`,
                  }}
                />

                {itensExtrato.map((item, index) => {
                  const cor = corStatus(
                    item.status_agendamento,
                  );

                  const horarioEvento =
                    getHorarioEvento(item);

                  const executor =
                    item.nome_usuario ||
                    item.usuario_nome ||
                    "Não identificado";

                  const ehUltimo =
                    inicioExtrato + index === 0;

                  const observacao =
                    item.observacoes ||
                    item.observacao ||
                    "";

                  const podeExcluir =
                    canDeleteAg(
                      item.data_agendamento,
                    ) && item.id;

                  return (
                    <div
                      key={
                        item.id ||
                        `${item.data_agendamento}-${index}`
                      }
                      className="relative"
                    >
                      <span
                        className="absolute -left-[22px] top-[17px] h-[11px] w-[11px] rounded-full"
                        style={{
                          background: cor.color,
                          boxShadow: ehUltimo
                            ? `0 0 0 4px ${cor.color}22`
                            : "0 0 0 3px #fff",
                        }}
                      />

                      <div
                        className="grid grid-cols-[110px_minmax(0,1fr)_auto] items-center gap-3.5 rounded-xl border-l-[3px] px-4 py-3.5"
                        style={{
                          background: ehUltimo
                            ? "#F9F8FE"
                            : "#fff",
                          borderColor: ehUltimo
                            ? "#E3DFFA"
                            : C.line,
                          borderLeftColor: cor.color,
                        }}
                      >
                        <div className="min-w-0">
                          <div
                            className="text-[9.5px] font-bold uppercase tracking-wide"
                            style={{
                              color: ehUltimo
                                ? C.accent
                                : C.textMuted,
                            }}
                          >
                            {ehUltimo
                              ? "Último evento"
                              : `Evento #${item.id || "—"}`}
                          </div>

                          <div
                            className="mt-1 text-[11.5px] font-bold"
                            style={{ color: C.text }}
                          >
                            {formatDataHora(horarioEvento)}
                          </div>
                        </div>

                        <div className="min-w-0">
                          <div className="flex min-w-0 flex-wrap items-center gap-2">
                            <span
                              className="inline-flex shrink-0 rounded-full px-2.5 py-0.5 text-[10px] font-bold"
                              style={{
                                color: cor.color,
                                background: `${cor.color}18`,
                              }}
                            >
                              {item.status_agendamento ||
                                "—"}
                            </span>

                            <span
                              className="truncate text-[11.5px]"
                              style={{
                                color: C.textSoft,
                              }}
                            >
                              Consulta:{" "}
                              <strong
                                style={{
                                  color: C.text,
                                }}
                              >
                                {formatDataHora(
                                  item.data_agendamento,
                                )}
                              </strong>
                            </span>
                          </div>

                          <div
                            className="mt-1 truncate text-[10.5px]"
                            style={{
                              color: C.textMuted,
                            }}
                            title={executor}
                          >
                            Executado por:{" "}
                            <strong
                              style={{
                                color: C.textSoft,
                              }}
                            >
                              {executor}
                            </strong>
                          </div>

                          {observacao ? (
                            <div
                              className="mt-1 truncate text-[10px]"
                              style={{
                                color: C.textMuted,
                              }}
                              title={observacao}
                            >
                              Obs.: {observacao}
                            </div>
                          ) : null}
                        </div>

                        {podeExcluir ? (
                          <button
                            type="button"
                            onClick={() =>
                              excluirAgendamento(item.id)
                            }
                            disabled={salvando}
                            className="flex items-center gap-1.5 rounded-lg px-2.5 py-1.5 text-[10.5px] font-bold transition hover:brightness-95 disabled:opacity-60"
                            style={{
                              background: "#FBE9EC",
                              color: C.rose,
                            }}
                          >
                            <Trash2 size={12} />
                            Excluir
                          </button>
                        ) : (
                          <span
                            className="px-2 text-[9.5px] font-semibold"
                            style={{
                              color: C.textMuted,
                            }}
                          >
                            —
                          </span>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}

            {historicoOrdenado.length >
            ITENS_EXTRATO ? (
              <div className="mt-3 flex items-center justify-between gap-3">
                <span
                  className="text-[10.5px] font-semibold"
                  style={{ color: C.textMuted }}
                >
                  Mostrando {inicioExtrato + 1}–
                  {Math.min(
                    inicioExtrato + ITENS_EXTRATO,
                    historicoOrdenado.length,
                  )}{" "}
                  de {historicoOrdenado.length}
                </span>

                <div className="flex items-center gap-1.5">
                  <button
                    type="button"
                    disabled={
                      paginaExtratoSegura <= 1
                    }
                    onClick={() =>
                      setPaginaExtrato((pagina) =>
                        Math.max(1, pagina - 1),
                      )
                    }
                    className="flex h-7 w-7 items-center justify-center rounded-md disabled:opacity-35"
                    style={{
                      border: `1px solid ${C.line}`,
                      color: C.textSoft,
                      background: C.panel,
                    }}
                  >
                    <ChevronLeft size={14} />
                  </button>

                  <span
                    className="min-w-[46px] text-center text-[10px] font-bold"
                    style={{ color: C.textSoft }}
                  >
                    {paginaExtratoSegura}/
                    {totalPaginasExtrato}
                  </span>

                  <button
                    type="button"
                    disabled={
                      paginaExtratoSegura >=
                      totalPaginasExtrato
                    }
                    onClick={() =>
                      setPaginaExtrato((pagina) =>
                        Math.min(
                          totalPaginasExtrato,
                          pagina + 1,
                        ),
                      )
                    }
                    className="flex h-7 w-7 items-center justify-center rounded-md disabled:opacity-35"
                    style={{
                      border: `1px solid ${C.line}`,
                      color: C.textSoft,
                      background: C.panel,
                    }}
                  >
                    <ChevronRight size={14} />
                  </button>
                </div>
              </div>
            ) : null}

            {usuarioEhAdministrador ? (
              <div
                className="mt-6 pt-4"
                style={{
                  borderTop: `1px solid ${C.lineSoft}`,
                }}
              >
                <button
                  type="button"
                  onClick={() =>
                    setAuditoriaAberta(
                      (aberta) => !aberta,
                    )
                  }
                  className="flex w-full items-center justify-between gap-3 rounded-xl px-3.5 py-3 text-left transition hover:brightness-[0.98]"
                  style={{
                    background: C.panel,
                    border: `1px solid ${C.line}`,
                  }}
                >
                  <div className="flex min-w-0 items-center gap-2.5">
                    <ShieldCheck
                      size={15}
                      color={C.accent}
                    />

                    <span
                      className="text-[12.5px] font-bold"
                      style={{ color: C.text }}
                    >
                      Auditoria técnica
                    </span>

                    <span
                      className="rounded-full px-2.5 py-0.5 text-[10px] font-bold"
                      style={{
                        background: "#EEEBFB",
                        color: C.accent,
                      }}
                    >
                      {auditoriaOrdenada.length}
                    </span>

                    <span
                      className="hidden text-[10.5px] sm:inline"
                      style={{
                        color: C.textMuted,
                      }}
                    >
                      somente administradores
                    </span>
                  </div>

                  <ChevronDown
                    size={15}
                    color={C.textSoft}
                    style={{
                      transform: auditoriaAberta
                        ? "rotate(180deg)"
                        : "rotate(0deg)",
                      transition:
                        "transform 150ms ease",
                    }}
                  />
                </button>

                {auditoriaAberta ? (
                  <div className="mt-2 space-y-2">
                    {auditoriaOrdenada.length ===
                    0 ? (
                      <div
                        className="rounded-xl border-[1.5px] border-dashed px-3.5 py-3 text-[11px] italic"
                        style={{
                          color: C.textMuted,
                          borderColor: C.line,
                        }}
                      >
                        Nenhuma alteração auditada ainda.
                      </div>
                    ) : (
                      <>
                        {itensAuditoria.map((item) => {
                          const mudanca =
                            descricaoMudancaAuditoria(
                              item,
                            );

                          const corNovo =
                            corStatus(
                              mudanca.statusNovo,
                            ).color;

                          return (
                            <div
                              key={
                                item.id ||
                                `${item.criado_em}-${mudanca.statusNovo}`
                              }
                              className="grid grid-cols-[110px_minmax(0,1fr)] gap-3.5 rounded-xl px-4 py-3"
                              style={{
                                background: "#fff",
                                border: `1px solid ${C.line}`,
                              }}
                            >
                              <div
                                className="text-[10px] font-bold"
                                style={{
                                  color: C.textMuted,
                                }}
                              >
                                {formatDataHora(
                                  item.criado_em,
                                )}
                              </div>

                              <div className="min-w-0">
                                <div
                                  className="truncate text-[11px] font-bold"
                                  style={{
                                    color: C.text,
                                  }}
                                >
                                  {mudanca.statusAnterior}{" "}
                                  <span
                                    style={{
                                      color:
                                        C.textMuted,
                                    }}
                                  >
                                    →
                                  </span>{" "}
                                  <span
                                    style={{
                                      color:
                                        corNovo,
                                    }}
                                  >
                                    {mudanca.statusNovo}
                                  </span>
                                </div>

                                {mudanca.dataAnterior ||
                                mudanca.dataNova ? (
                                  <div
                                    className="mt-0.5 truncate text-[10px]"
                                    style={{
                                      color:
                                        C.textMuted,
                                    }}
                                    title={`Consulta anterior: ${formatDataHora(
                                      mudanca.dataAnterior,
                                    )} | nova: ${formatDataHora(
                                      mudanca.dataNova,
                                    )}`}
                                  >
                                    Consulta:{" "}
                                    {formatDataCurta(
                                      mudanca.dataAnterior,
                                    )}{" "}
                                    →{" "}
                                    {formatDataCurta(
                                      mudanca.dataNova,
                                    )}
                                  </div>
                                ) : null}

                                <div
                                  className="mt-0.5 truncate text-[10px]"
                                  style={{
                                    color:
                                      C.textMuted,
                                  }}
                                >
                                  Alterado por:{" "}
                                  <strong
                                    style={{
                                      color:
                                        C.textSoft,
                                    }}
                                  >
                                    {item.usuario_nome ||
                                      item.nome_usuario ||
                                      "Sistema"}
                                  </strong>
                                  {item.origem
                                    ? ` · ${item.origem}`
                                    : ""}
                                </div>
                              </div>
                            </div>
                          );
                        })}

                        {auditoriaOrdenada.length >
                        ITENS_AUDITORIA ? (
                          <div className="flex items-center justify-end gap-1.5">
                            <button
                              type="button"
                              disabled={
                                paginaAuditoriaSegura <=
                                1
                              }
                              onClick={() =>
                                setPaginaAuditoria(
                                  (pagina) =>
                                    Math.max(
                                      1,
                                      pagina - 1,
                                    ),
                                )
                              }
                              className="flex h-6 w-6 items-center justify-center rounded-md disabled:opacity-35"
                              style={{
                                border: `1px solid ${C.line}`,
                                color:
                                  C.textSoft,
                                background:
                                  C.panel,
                              }}
                            >
                              <ChevronLeft
                                size={12}
                              />
                            </button>

                            <span
                              className="min-w-[42px] text-center text-[10px] font-bold"
                              style={{
                                color:
                                  C.textSoft,
                              }}
                            >
                              {paginaAuditoriaSegura}/
                              {totalPaginasAuditoria}
                            </span>

                            <button
                              type="button"
                              disabled={
                                paginaAuditoriaSegura >=
                                totalPaginasAuditoria
                              }
                              onClick={() =>
                                setPaginaAuditoria(
                                  (pagina) =>
                                    Math.min(
                                      totalPaginasAuditoria,
                                      pagina + 1,
                                    ),
                                )
                              }
                              className="flex h-6 w-6 items-center justify-center rounded-md disabled:opacity-35"
                              style={{
                                border: `1px solid ${C.line}`,
                                color:
                                  C.textSoft,
                                background:
                                  C.panel,
                              }}
                            >
                              <ChevronRight
                                size={12}
                              />
                            </button>
                          </div>
                        ) : null}
                      </>
                    )}
                  </div>
                ) : null}
              </div>
            ) : null}
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}
